import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {ConfirmationService, MenuItem, MessageService, TreeNode} from 'primeng/api';
import {EditorComponent} from '@tinymce/tinymce-angular';
import {TeVariableComponentBase} from '../../../logic/te-variable-component-base';
import {TinymceAutosave} from '../../../logic/tinymce-autosave';
import {TeAutosaveCookieDto} from '../../../model/dto/entity/te-autosave-cookie.dto';
import {debounceTime, fromEvent, Subject, Subscription} from 'rxjs';
import {TeContentTypeEnum} from "../../../enum/te-content-type.enum";
import {TeEditorTypeEnum} from "../../../enum/te-editor-type.enum";
import type {TableInfoConditionsEvent} from "./components/table-info-conditions/table-info-conditions.component";
import {TestCaseChangeEvent} from "../te-test-case/te-test-case.component";
import {TeTemplateService} from "../../../service/api/te-template.service";
import {DomSanitizer, SafeHtml} from "@angular/platform-browser";
import {CodeMirrorEditorComponent} from "./components/code-mirror-editor/code-mirror-editor.component";
import {MetaDataAttributeDto} from "../../../../../core/shared/meta/dto/meta-data-attribute.dto";
import {MetaService} from "../../../../../core/shared/meta/service/meta.service";
import {MvsCoreService} from "../../../../../core/shared/service/mvs-core.service";
import {MvsCrudObjectHistoryService} from "../../../../../core/shared/crud/service/mvs-crud-object-history.service";
import {MvsMessageService} from "../../../../../core/shared/service/message/mvs-message.service";
import {MvsUiObjectService} from "../../../../../core/shared/object/service/mvs-ui-object.service";
import {TeEditorCommunicationService, TeEditorCommand} from '../../../service/te-editor-communication.service';

export enum variableTypesEnum {
    SIMPLE,
    CHANGEABLE,
    CHECK_NULL,
    DATE_FIELD
}

export interface TableAttributes {
    attribute: string;
    value: string;
    subPath: string;
}

export interface TeEditorSaveEvent {
    content: string;
    editorTypeEnum: TeEditorTypeEnum;
}

interface ConditionHighlighterEvent {
    info: string,
    condition: string,
    targetElement: HTMLElement,
    editor: any
}

@Component({
    selector: 'mvs-te-editor',
    templateUrl: './te-editor.component.html',
    styleUrls: ['./te-editor.component.scss'],
    standalone: false
})
export class TeEditorComponent extends TeVariableComponentBase
    implements OnInit, OnChanges, OnDestroy {

    readonly ContentTypeEnum = TeContentTypeEnum;

    busy: boolean;  // indicator whether the component is busy
    initialized: boolean; // indicator whether the component was initialized
    nodeChildrenAttributes: MetaDataAttributeDto[] = [];
    dragAttributeChild: string;
    tinyMceConfig: object;
    openVariableDialog: boolean = false;


    isContentChanged: boolean = false;
    isDirty: boolean = false;
    isLegacyEditorMode: boolean = false;

    editor: "tinyMCE" | "textArea" = "tinyMCE";

    editorModeOptions: { label: string, value: string, disabled?: boolean }[] = [
        { label: 'Visueller Editor', value: 'tinyMCE' },
        { label: 'Code', value: 'textArea' }
    ];

    editorContent: string;

    // Variable fallback dialog state
    showFallbackDialog: boolean = false;
    fallbackDialogPath: string = '';
    fallbackDialogValue: string = '';
    private fallbackDialogChipElement: HTMLElement | null = null;

    // Chip interaction dialog state
    showIfDialog: boolean = false;
    ifDialogMode: 'insert' | 'tableColumn' | 'editChip' = 'insert';
    ifDialogVarPath: string = '';
    ifDialogLabel: string = '';
    ifDialogDataType: string = 'string';
    ifDialogEnumValues: string[] = [];
    ifDialogPreselectedOption: string = '';
    editingChipElement: HTMLElement = null;

    showTableBuilderDialog: boolean = false;
    tableBuilderEntityAlias: string = '';
    tableBuilderEntityId: number = 0;
    tableBuilderCollectionPath: string = '';
    tableBuilderItemAlias: string = '';
    tableBuilderCollectionLabel: string = '';
    tableBuilderEditMode: boolean = false;
    tableBuilderExistingColumns: any[] = [];

    showTableConditionDialog: boolean = false;
    showHighlightConditionDialog: boolean = false;
    tableObjectName: string;
    tableObjectTypeName: string;
    tableAttributes: TableAttributes[];
    changedTableAttributes: TableAttributes[];
    tableCondition: string;
    highlightCondition: string;
    tableAttributePath: string;
    tableNode: any;
    tableName: string;
    toolbarButtonList: MenuItem[];
    inlineConditions: ConditionHighlighterEvent;

    widgetDataSubscription: Subscription;
    copiedText: string;

    // HTML Live-Preview (raw template preview in code mode)
    showPreview: boolean = false;
    previewHtml: SafeHtml;
    private previewSubject = new Subject<string>();
    private commandSubscription: Subscription;

    // Test case generated preview
    showTestPreview: boolean = false;

    @Input() content: string;
    @Input() selectedTestCase: TestCaseChangeEvent;
    @Input() editorTypeEnum: TeEditorTypeEnum;
    @Input() artefactMap: Record<number, string> = {};
    @Input() previewEnabled: boolean = true;
    @Input() testPreviewHtml: SafeHtml;
    @Input() testPreviewPdf: SafeHtml;
    @Input() testPreviewBusy: boolean = false;
    @Input() testPreviewError: string;
    @Input() testPreviewErrorDetails: string;
    @Output() onSave: EventEmitter<TeEditorSaveEvent> = new EventEmitter<TeEditorSaveEvent>();
    @Output() onGeneratePreview: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('tinyEditor') tinyEditor!: EditorComponent;
    @ViewChild(CodeMirrorEditorComponent) codeMirrorEditor!: CodeMirrorEditorComponent;

    private autoSave = new TinymceAutosave();

    constructor(
        protected metaService: MetaService,
        protected coreService: MvsCoreService,
        protected toast: MessageService,
        protected templateService: TeTemplateService,
        protected objectHistory: MvsCrudObjectHistoryService,
        protected messageService: MvsMessageService,
        protected objectService: MvsUiObjectService,
        public sanitizer: DomSanitizer,
        protected confirmationService: ConfirmationService,
        private editorCommunicationService: TeEditorCommunicationService) {
        super();
    }

    ngOnInit(): void {
        this.initComponent();
        this.refreshComponent();

        // HTML Live-Preview debounce setup
        this.previewSubject.pipe(
            debounceTime(500)
        ).subscribe(content => {
            this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(content);
        });

        this.toolbarButtonList = [
            {
                label: 'Verlauf anzeigen',
                icon: 'fa-regular fa-clock-rotate-left',
                command: () => {
                    this.showHistory();
                }
            },
            {
                label: 'Freemarker Dokumentation',
                icon: 'fa-regular fa-file-contract',
                command: () => {
                    this.navigateToFreeMarker();
                }
            },
        ];
    }

    public openVariables() {
        this.openVariableDialog = true;
    }

    handleSave() {

        let content = undefined;

        if (this.editor == "tinyMCE") {
            content = this.tinyEditor.editor.getContent({format: "html"});
        } else {
            content = this.editorContent;
        }

        const editorType = this.editor === 'tinyMCE' ? TeEditorTypeEnum.WYSIWYG : TeEditorTypeEnum.CODE;

        this.onSave.emit({ content, editorTypeEnum: editorType });
        this.isDirty = false;
        this.isLegacyEditorMode = false;
    }

    /**
     * Initialize Component.
     */
    initComponent() {
        this.configureTinyMce();

        // Subscribe to editor commands from communication service
        this.commandSubscription = this.editorCommunicationService.command$.subscribe(
            (command: TeEditorCommand) => this.handleEditorCommand(command)
        );

        this.objectService.widgetSelectData.subscribe(res => {
            const artefactContent = res['artefactDto'].content;
            this.insertTextIntoPosition(artefactContent);
        });

        this.autoSave.getAutoSaveContent().subscribe({
            next: (content) => {
                if (this.isContentChanged) {
                    const editorType = this.editor === 'tinyMCE' ? TeEditorTypeEnum.WYSIWYG : TeEditorTypeEnum.CODE;
                    this.onSave.emit({ content, editorTypeEnum: editorType });
                }
            },
            error: (error) => console.log(error)
        });
    }

    /**
     * Handle commands from the editor communication service.
     * Routes commands to TinyMCE or CodeMirror depending on the active editor.
     */
    private handleEditorCommand(command: TeEditorCommand): void {
        switch (command.type) {
            case 'insertVariable':
                if (this.editor === 'tinyMCE' && this.tinyEditor?.editor) {
                    this.tinyEditor.editor.execCommand('teInsertVariable', false, command.path);
                } else {
                    this.insertTextIntoPosition('${' + command.path + '}');
                }
                break;
            case 'insertArtefact':
                if (this.editor === 'tinyMCE' && this.tinyEditor?.editor) {
                    this.tinyEditor.editor.execCommand('teInsertArtefact', false, { id: command.id, name: command.name });
                } else {
                    this.insertTextIntoPosition('#artefact:' + command.id + '#');
                }
                break;
            case 'insertFreeMarker':
                this.insertAsChipsOrRaw(command.code);
                break;
            case 'insertTable':
                this.insertAsChipsOrRaw(command.html);
                break;
            case 'openTableBuilder':
                this.tableBuilderEntityAlias = command.entityAlias;
                this.tableBuilderEntityId = command.entityId;
                this.tableBuilderCollectionPath = command.collectionPath;
                this.tableBuilderItemAlias = command.itemAlias;
                this.tableBuilderCollectionLabel = command.collectionLabel;
                this.tableBuilderEditMode = false;
                this.tableBuilderExistingColumns = [];
                this.showTableBuilderDialog = true;
                break;
            case 'openIfDialog':
                this.ifDialogMode = 'insert';
                this.ifDialogVarPath = command.varPath;
                this.ifDialogLabel = command.label;
                this.ifDialogDataType = command.dataType;
                this.ifDialogEnumValues = command.enumValues || [];
                this.ifDialogPreselectedOption = '';
                this.editingChipElement = null;
                this.showIfDialog = true;
                break;
            case 'replaceAll':
                this.replaceAllInContent(command.search, command.replace);
                break;
        }
    }

    /**
     * Replace all occurrences of a search string in the current editor content.
     * Works for both TinyMCE and CodeMirror modes.
     */
    private replaceAllInContent(search: string, replace: string): void {
        if (!search || search === replace) return;

        const escapedSearch = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const pattern = new RegExp(escapedSearch, 'g');

        if (this.editor === 'tinyMCE' && this.tinyEditor?.editor) {
            // Get raw FreeMarker content (toF is applied by GetContent handler)
            let content = this.tinyEditor.editor.getContent({ format: 'html' });
            content = content.replace(pattern, replace);
            this.tinyEditor.editor.setContent(content);
            this.isDirty = true;
        } else {
            // CodeMirror mode
            if (this.editorContent) {
                this.editorContent = this.editorContent.replace(pattern, replace);
                this.isDirty = true;
            }
        }
    }

    /**
     * Refresh Component.
     */
    refreshComponent() {
        // create form control
        this.editorContent = this.content;

        // Plain text → always Code editor, disable visual editor option
        const isPlain = this.contentType === TeContentTypeEnum.plain_text;
        this.editorModeOptions = [
            { label: 'Visueller Editor', value: 'tinyMCE', disabled: isPlain },
            { label: 'Code', value: 'textArea' }
        ];

        if (isPlain) {
            // Plain text content → always Code editor, never tinyMCE
            this.editor = 'textArea';
            this.isLegacyEditorMode = false;
        } else if (this.editorTypeEnum != null) {
            // Explicit editor type set — respect it
            this.editor = this.editorTypeEnum === TeEditorTypeEnum.CODE ? 'textArea' : 'tinyMCE';
            this.isLegacyEditorMode = false;
        } else if (this.contentType === TeContentTypeEnum.xhtml) {
            // XHTML content → Code editor (TinyMCE would mangle it)
            this.editor = 'textArea';
            this.isLegacyEditorMode = false;
        } else if (this.forceKeepCodeMirrorEditor()) {
            // Auto-detected as code content
            this.editor = 'textArea';
            this.isLegacyEditorMode = true;
        } else {
            // Default: use WYSIWYG but show legacy mode banner
            this.isLegacyEditorMode = true;
        }

        this.checkUpdatedContentWithInCookie();

        this.initialized = true;
    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (!this.initialized) {
            return;
        }

        if (changes["id"]) {
            this.refreshComponent();
        }
    }

    configureTinyMce(): void {
        const component = this;
        this.tinyMceConfig = {
            selector: "#templateEngineTinyEditor",
            contextmenu_never_use_native: true,
            contextmenu: 'copy paste',
            base_url: '/assets/plugin/js/tinymce',
            suffix: '.min',
            menubar: true,
            branding: false,
            promotion: false,
            height: 'calc(100vh - 220px)',
            inline: false,
            paste_block_drop: false,
            paste_as_text: true,


            // --> INSERT MARKO -> TRY TO DISABLE THE CHECKS AND REPLACEMENTS
            valid_elements: '*[*]',  // Allows all elements and attributes
            extended_valid_elements: 'img[class|src|border=0|alt|title|hspace|vspace|width|height|align|name|style|span[data-info]]',
            // --> END INSERT MARKO -> TRY TO DISABLE THE CHECKS AND REPLACEMENTS

            external_plugins: {
                teVariableChip: '/assets/plugin/js/te-variable-chip.plugin.js'
            },
            plugins: 'lists link image table code help wordcount emoticons teVariableChip',
            toolbar: 'copy undo redo formatselect | bold italic strikethrough codesample forecolor backcolor | link image | alignleft aligncenter alignright alignjustify | numlist bullist outdent indent | removeformat hr pagebreak code | emoticons',
            file_picker_types: 'image',
            file_picker_callback: (callback, value, meta) => {
                if (meta.filetype === 'image') {
                    const input = document.createElement('input');
                    input.setAttribute('type', 'file');
                    input.setAttribute('accept', 'image/*');
                    input.addEventListener('change', () => {
                        const file = input.files[0];
                        if (file) {
                            const reader = new FileReader();
                            reader.onload = () => {
                                callback(reader.result as string, { alt: file.name });
                            };
                            reader.readAsDataURL(file);
                        }
                    });
                    input.click();
                }
            },
            table_toolbar: 'tableprops tabledelete | tableConditions',
            setup: (editor) => { 

                // Pass artefact name map to the teVariableChip plugin
                try {
                    editor.options.set('teArtefactMap', component.artefactMap || {});
                } catch (e) {
                    // Fallback for older TinyMCE versions
                    editor.settings = editor.settings || {};
                    editor.settings.teArtefactMap = component.artefactMap || {};
                }

                let currentTable = null;

                editor.on('input change', () => {
                    this.isContentChanged = true;
                    this.isDirty = true;
                    /*
                    TODO: The auto save needs to be reimplemented
                    this.autoSave.setAlias(this.alias+this.templateId);
                    this.autoSave.autoSaveEditorContent(this.tinyEditor.editor.getContent());
                      this.setContentIntoCookie(editor);
                     */
                });

                editor.on('NodeChange', function (e) {
                    const node = e.element;
                    currentTable = node.closest('table');
                });

                editor.ui.registry.addButton('tableInfo', {
                    tooltip: 'Tabellen-Info',
                    icon: 'info',
                    onAction: function () {
                        if (currentTable) {
                            const objectName = currentTable.getAttribute('data-object-name') || 'Kein Objektname gesetzt';
                            component.tableObjectName = objectName;
                            editor.windowManager.open({
                                title: 'Tabellen-Information',
                                body: {
                                    type: 'panel',
                                    items: [
                                        {
                                            type: 'htmlpanel',
                                            html: `
                                                    <p>Objektname: ${objectName}</p>
                                                    <p>Hat Bedingungen? Nein</p>
`
                                        }
                                    ]
                                },
                                buttons: [
                                    {
                                        type: 'cancel',
                                        text: 'Schließen'
                                    }
                                ]
                            });
                        }
                    }
                });

                editor.ui.registry.addButton('tableConditions', {
                    // text: 'Add conditions',
                    icon: 'settings',
                    tooltip: 'Filter hinzufügen',
                    onAction: function () {
                        const selectedNode = editor.selection.getNode();
                        const tableNode = selectedNode.closest('table');


                        if (tableNode) {
                            // self.addConditionsToTable(editor, tableNode);
                            component.extractAttributes(tableNode);
                            editor.getBody().blur(); //hide the toolbar
                            // self.extractCondition(tableNode);
                        }
                    }
                });

                // Chip double-click: open edit dialog based on chip type
                editor.on('teChipDblClick', (event) => {
                    const detail = event.detail || event;
                    if (!detail) return;

                    switch (detail.chipType) {
                        case 'variable':
                            const chipEl = detail.element;
                            if (chipEl) {
                                let expr: string;
                                try { expr = atob(chipEl.getAttribute('data-te-expr') || ''); } catch { expr = chipEl.getAttribute('data-te-expr') || ''; }
                                const match = expr.match(/^\$\{([^!}]+)(?:!"([^"]*)")?\s*\}$/);
                                if (match) {
                                    this.fallbackDialogPath = match[1];
                                    this.fallbackDialogValue = match[2] || '';
                                    this.fallbackDialogChipElement = chipEl;
                                    this.showFallbackDialog = true;
                                }
                            }
                            break;
                        case 'if':
                            this.ifDialogMode = 'editChip';
                            this.editingChipElement = detail.element;

                            // detail.expr is the parsed condition (e.g. "customer.active??" or "status == \"active\"")
                            const ifExpr = detail.expr || '';
                            // Extract varPath by stripping ?? and == comparisons
                            let varPath = ifExpr.replace(/\?\?$/, '').replace(/\s*==\s*".*"$/, '').trim();

                            // Detect preselected option from the expression
                            let preOpt = 'exists';
                            if (ifExpr.indexOf('??') === -1 && ifExpr.indexOf('==') === -1) {
                                preOpt = 'ifTrue'; // bare variable → boolean
                            } else if (ifExpr.indexOf('==') > -1) {
                                const eqMatch = ifExpr.match(/==\s*"([^"]*)"/);
                                if (eqMatch) preOpt = 'enum_' + eqMatch[1];
                            }

                            this.ifDialogVarPath = varPath;
                            this.ifDialogLabel = varPath;
                            this.ifDialogDataType = 'string';
                            this.ifDialogEnumValues = [];
                            this.ifDialogPreselectedOption = preOpt;
                            this.showIfDialog = true;
                            break;
                        case 'artefact':
                            // Could open artefact detail — for now, no-op
                            break;
                    }
                });

                // Table double-click: open table builder in edit mode
                editor.on('teTableDblClick', (event) => {
                    const detail = event.detail || event;
                    if (!detail || !detail.tableElement) return;

                    this.editingChipElement = detail.tableElement;

                    // Extract loop/guard info from the event (populated by plugin)
                    const loopPath = detail.loopExpr || '';
                    const loopAlias = detail.loopAlias || '';

                    this.tableBuilderCollectionPath = loopPath;
                    this.tableBuilderItemAlias = loopAlias;
                    this.tableBuilderCollectionLabel = loopPath;

                    // Try to resolve entityAlias from the itemAlias or loop path
                    // The entityAlias was stored on the original insert; for edit we
                    // extract it from data-te-entity-alias if available, otherwise
                    // leave it for the dialog to resolve via entityId.
                    this.tableBuilderEntityAlias = detail.tableElement.getAttribute('data-te-entity-alias') || '';
                    this.tableBuilderEntityId = parseInt(detail.tableElement.getAttribute('data-te-entity-id') || '0', 10);

                    // Extract existing columns from the table for pre-population.
                    // The plugin sends { header, cellTemplate } for each column.
                    // Convert cellTemplate to attrName by extracting the path from ${path}.
                    const rawCols = detail.existingColumns || [];
                    this.tableBuilderExistingColumns = rawCols.map((col: any) => {
                        let attrName = col.cellTemplate || '';
                        // Extract path from ${path} or ${path!"fallback"}
                        const varMatch = attrName.match(/^\$\{([^}!]+)/);
                        if (varMatch) {
                            attrName = varMatch[1].trim();
                        }
                        return {
                            header: col.header || '',
                            attrName: attrName,
                            cellTemplate: col.cellTemplate || ''
                        };
                    });

                    this.tableBuilderEditMode = true;
                    this.showTableBuilderDialog = true;
                });

            }
        };
    }

    onHighlightClick(event: ConditionHighlighterEvent, editor: any) {
        this.inlineConditions = event;
        this.inlineConditions.editor = editor;
        this.tableAttributePath = 'cr_CustomerContract_attr_customer';
        this.showHighlightConditionDialog = true;
    }

    updateInlineCondition(newCondition: string, targetElement: HTMLElement, editor: any) {
        targetElement.innerText = newCondition;
        let htmlContent = editor.getContent();
        editor.setContent(htmlContent);
    }

    extractAttributes(tableNode: any) {

        //extract table name
        const objectName = tableNode.getAttribute('data-object-name')
        const parentObjectName = tableNode.getAttribute('data-parent-object-name');
        this.tableObjectTypeName = parentObjectName;
        this.tableName = objectName;
        this.tableNode = tableNode;
        this.dragAttributeChild = tableNode.getAttribute('drag-attribute-child');

        if (!objectName) {
            return;
        }

        this.tableAttributes = this.extractTableData(tableNode);
        this.prepareTableDialog(tableNode);


    }

    extractTableData(tableNode: HTMLTableElement): Array<TableAttributes> {
        tableNode.innerHTML = this.removeHighlights(tableNode.innerHTML);
        let headers = tableNode.querySelectorAll('thead th');
        let rows = tableNode.querySelectorAll('tbody tr');
        let tableData = [];

        // Assuming each row has the same number of cells as there are headers
        rows.forEach(row => {
            let cells = row.querySelectorAll('td');
            cells.forEach((cell, index) => {
                let header = headers[index].textContent.trim();  // Get the header name
                let valueContainerSpan = cell.querySelector('span'); // Assuming the value is in a <span> element
                let valueContainer = cell.innerHTML; //direct text in cell
                let value = valueContainerSpan ? valueContainerSpan.textContent : valueContainer;

                tableData.push({
                    attribute: header,
                    value: value
                });
            });
        });

        return tableData;
    }

    prepareTableDialog(tableNode: any) {
        tableNode.innerHTML = this.removeHighlights(tableNode.innerHTML);
        const tBody = tableNode.querySelector('tbody');

        if (!tBody) {
            return;
        }

        const bodyInnerHtml = tBody.innerHTML;
        this.tableCondition = this.extractConditionFromString(bodyInnerHtml);
        this.tableAttributePath = this.extractAttributePath(bodyInnerHtml);

        this.showTableConditionDialog = true;
    }

    extractAttributePath(htmlString: string): string | null {

        //remove highlighting conditions from string
        htmlString = this.removeHighlights(htmlString);

        // Adjusting the regex to be more flexible with spaces and to correctly stop before the comment closure
        const listRegex = /<!--\s*<#list\s+[^ ]+\s+as\s+([^ ]+?)\s*(?=-->)/;

        let newString = '';

        const match = htmlString.match(listRegex);
        newString = match[1];

        if (newString.endsWith('>')) {
            const newMatch = match[1].split('>')
            newString = newMatch[0];
        }

        if (newString) {
            // Return the captured attribute path without trailing characters
            return newString.trim();  // Trim any accidental whitespace capture for safety
        } else {
            return null;
        }
    }


    extractConditionFromString(htmlString: string) {
        const regex = /<!--\s*<#if(.*?)>\s*-->/s;

        const match = htmlString.match(regex);

        if (match && match[1]) {
            return `<#if ${match[1]} >`;
        } else {
            return null;
        }
    }

    addConditionsToTable(newCondition: string) {
        const tbody = this.tableNode.querySelector('tbody');
        if (tbody) {
            // let tbodyHTML = this.restoreOriginalHtml(tbody.innerHTML);
            let tbodyHTML = tbody.innerHTML;
            tbodyHTML = this.removeHighlights(tbodyHTML);

            // Check for the existing condition by looking for the closing tag.
            let hasExistingCondition = false;

            if (tbodyHTML.includes(`<!--&l<!--&lt;/#if&gt;-->`) || tbodyHTML.includes("<!--</#if>-->")) {
                hasExistingCondition = true;
            }

            if (hasExistingCondition) {
                // Regex to find and capture the existing condition expression for replacement
                // This aims to capture only the expression within <#if ... >, excluding the content of the block
                let conditionExprRegex: RegExp;
                if (newCondition) {
                    conditionExprRegex = /(<!--\s*<#if).*?(>)/s;
                } else {
                    conditionExprRegex = /(<!--\s*<#if.*?>\s*-->)/s;
                }


                // Prepare the new condition expression for insertion, wrapped in comment tags

                let newConditionExpr = '';
                if (newCondition) {
                    newConditionExpr = `<!-- ${newCondition} `;
                } else {
                    newConditionExpr = '';
                }


                // Replace only the condition expression, preserving the content within the block
                tbodyHTML = tbodyHTML.replace(conditionExprRegex, newConditionExpr);

                if (!newCondition) {
                    // const searchString = /<!--<\/#if>-->/g;
                    const searchString = /<!--\s*(<\/?#if[^>]*>|&lt;\/?#if[^>]*&gt;)\s*-->/g;
                    const replacementString = '';

                    tbodyHTML = tbodyHTML.replace(searchString, replacementString);
                }
            } else {
                //If no existing condition, simply append the new condition at the end before </#list>
                const conditionStartTag = `<!-- ${newCondition}-->`;
                const conditionEndTag = "<!--</#if>-->";
                // Identify the positions for the list and tr tags
                const listStartTag = "<!--<#list";
                const listEndTag = "<!--</#list>-->";
                const listEndTagAlt = "<!--&lt;/#list&gt;-->";


                const listStartIndex = tbodyHTML.indexOf(listStartTag);
                // const listEndIndex = tbodyHTML.lastIndexOf(listEndTag) + listEndTag.length;
                let listEndIndex = tbodyHTML.indexOf(listEndTag);
                if (listEndIndex === -1) {
                    listEndIndex = tbodyHTML.indexOf(listEndTagAlt);
                    if (listEndIndex !== -1) {
                        listEndIndex += listEndTagAlt.length;
                    }
                } else {
                    listEndIndex += listEndTag.length;
                }

                const listTagCloseIndex = tbodyHTML.indexOf("-->", listStartIndex) + 3;
                const beforeList = tbodyHTML.substring(0, listTagCloseIndex);
                const afterListAndBeforeTrs = tbodyHTML.substring(listTagCloseIndex, listEndIndex - listEndTag.length);

                const afterTrs = tbodyHTML.substring(listEndIndex - listEndTag.length, listEndIndex);

                tbodyHTML = beforeList + conditionStartTag + afterListAndBeforeTrs + conditionEndTag + listEndTag;
                tbody.innerHTML = tbodyHTML;

            }

            // Update the tbody's HTML
            tbody.innerHTML = tbodyHTML;
        }
    }

    handleCloseDialog(isDialogClose: boolean): void {
        this.openVariableDialog = isDialogClose;
    }

    handleVariableSelect(selectedNode: TreeNode) {
        if (selectedNode["node"] && !selectedNode["node"].children) {
            (window as any).onVariableSelect(selectedNode);
        }
    }

    private checkUpdatedContentWithInCookie(): void {
        let editorContentFromCookie = this.autoSave.getAutoSaveCookie(this.alias + this.contentProviderId);
        if (editorContentFromCookie && this.editorContent !== editorContentFromCookie.content) {
            this.confirmationService.confirm({
                message: 'Möchten Sie den Inhalt mit der gespeicherten Version wiederherstellen?',
                icon: 'pi pi-exclamation-triangle',
                accept: () => {
                    this.isContentChanged = true;
                    this.editorContent = editorContentFromCookie?.content;
                    this.autoSave.autoSaveEditorContent(editorContentFromCookie?.content);
                },
                reject: () => {
                },
            });
        }
    }

    private setContentIntoCookie(editor: any) {

        //TODO: fromEvent implementation needs to check more cases/tests
        fromEvent(editor, 'input change').pipe(
            debounceTime(2000)
        ).subscribe(() => {
            const autoSaveCookieDto = new TeAutosaveCookieDto();
            autoSaveCookieDto.id = this.contentProviderId;
            autoSaveCookieDto.alias = this.alias;
            autoSaveCookieDto.content = editor.getContent();
            this.autoSave.setCookie(autoSaveCookieDto, this.alias + this.contentProviderId, 1);
        });
    }

    // add text at cursor location (raw, no chip conversion)
    private insertTextIntoPosition(value: string): void {
        if(this.editor === 'tinyMCE') {
            this.tinyEditor.editor.execCommand('mceInsertContent', false, value);
        } else {
            this.codeMirrorEditor.insertTextIntoPosition(value);
        }
    }

    /**
     * Insert FreeMarker content: in TinyMCE, convert to chips first via the plugin's
     * toC() function (triggered by firing a SetContent-like flow), in CodeMirror insert raw.
     */
    private insertAsChipsOrRaw(value: string): void {
        if (this.editor === 'tinyMCE' && this.tinyEditor?.editor) {
            // The teVariableChip plugin hooks into BeforeSetContent to convert FreeMarker to chips.
            // mceInsertContent does NOT trigger BeforeSetContent, so we need to manually convert.
            // Fire BeforeSetContent to let the plugin transform FreeMarker → chips.
            const evt: any = { content: value, format: 'html' };
            this.tinyEditor.editor.fire('BeforeSetContent', evt);
            this.tinyEditor.editor.execCommand('mceInsertContent', false, evt.content);
        } else {
            this.codeMirrorEditor.insertTextIntoPosition(value);
        }
    }

    navigateToFreeMarker() {
        const url = 'https://freemarker.apache.org/index.html';
        window.open(url, "_blank");
    }

    switchEditor() {
        if (this.editor === 'tinyMCE') {
            this.confirmationService.confirm({
                header: 'Editor wechseln?',
                message: 'Inhalte, die im WYSIWYG-Modus gespeichert wurden, sehen im Code-Editor möglicherweise anders aus. Dies ist sicher, aber möglicherweise schwerer lesbar.',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Trotzdem wechseln',
                rejectLabel: 'Abbrechen',
                rejectButtonStyleClass: 'p-button-outlined surface-0',
                accept: () => {
                    this.setCodeMirrorEditor();
                    this.editor = 'textArea';
                    this.editorCommunicationService.setActiveEditorMode('code');
                }
            });
        } else {
            this.confirmationService.confirm({
                header: 'Editor wechseln?',
                message: 'Wenn Ihr Inhalt FreeMarker-Ausdrücke oder benutzerdefiniertes HTML enthält, könnte der WYSIWYG-Editor die Formatierung verändern. Es wird empfohlen, zuerst den aktuellen Inhalt zu speichern.',
                icon: 'pi pi-exclamation-circle',
                acceptLabel: 'Trotzdem wechseln',
                rejectLabel: 'Abbrechen',
                rejectButtonStyleClass: 'p-button-outlined surface-0',
                accept: () => {
                    this.showPreview = false;
                    this.editor = 'tinyMCE';
                    this.editorCommunicationService.setActiveEditorMode('wysiwyg');
                }
            });
        }
    }

    onEditorModeChange(value: string) {
        if (!value || value === this.editor) {
            return;
        }

        if (value === 'block') {
            return;
        }

        this.switchEditor();
    }

    forceKeepCodeMirrorEditor(): boolean {
        if (!this.editorContent) return false;

        // If content starts with XML declaration or DOCTYPE, it's XHTML → code editor
        const trimmed = this.editorContent.trim();
        if (trimmed.startsWith('<?xml') || trimmed.startsWith('<!DOCTYPE')) {
            return true;
        }

        // If content has <html> tag wrapper, it's a full document → code editor
        if (trimmed.toLowerCase().startsWith('<html') || trimmed.toLowerCase().includes('<html>')) {
            return true;
        }

        return false;
    }

    handleInlineCondition(event: TableInfoConditionsEvent) {
        this.updateInlineCondition(event.condition, this.inlineConditions.targetElement, this.inlineConditions.editor);
        this.showHighlightConditionDialog = false;

    }

    setCodeMirrorEditor() {
        this.editorContent = this.tinyEditor.editor.getContent();
        this.editorContent = this.removeHighlights(this.editorContent);
    }

    handleTableCondition(event: TableInfoConditionsEvent) {
        this.addConditionsToTable(event.condition);
        this.changedTableAttributes = event.tableAttributes;
        this.updateTable(this.changedTableAttributes);
        this.showTableConditionDialog = false;

    }

    private updateTable(newAttributes: TableAttributes[]): void {

        // Update the header
        const thead = this.tableNode.querySelector('thead');
        const newHeaderRow = document.createElement('tr');
        newAttributes.forEach(attr => {
            const th = document.createElement('th');
            th.textContent = attr.attribute;
            newHeaderRow.appendChild(th);
        });
        thead.innerHTML = ''; // Clear old header row
        thead.appendChild(newHeaderRow);

        // Update the body row
        const tbodyRow = this.tableNode.querySelector('tbody tr');
        const newBodyRow = document.createElement('tr');
        newAttributes.forEach(attr => {
            const td = document.createElement('td');
            td.textContent = attr.value;
            newBodyRow.appendChild(td);
        });
        tbodyRow.parentNode.replaceChild(newBodyRow, tbodyRow); // Replace old row with new

        // Update current attributes with new
        this.tableAttributes = [...newAttributes];
    }

    handleCodeMirrorContentChange(event: string) {
        this.editorContent = event;
        this.isDirty = true;
        if (this.showPreview) {
            this.previewSubject.next(event);
        }
    }

    togglePreview() {
        this.showPreview = !this.showPreview;
        if (this.showPreview) {
            this.previewSubject.next(this.editorContent);
        }
    }

    executeTest() {
        this.showTestPreview = true;
        this.onGeneratePreview.emit();
    }

    showHistory() {
        if (!this.contentProviderId) {
            this.messageService.showErrorMessage('Fehler', 'Keine Vorlage geladen.');
            return;
        }

        try {
            this.objectHistory.showHistory('te.TeTemplate', this.contentProviderId, onLoadHistory => {
                this.busy = false;
            });
        } catch (e) {
            this.messageService.showErrorMessage('Fehler', 'Verlauf konnte nicht geladen werden.');
            console.error('showHistory error:', e);
        }
    }


    handleContentDisplay() {
    }



    removeHighlights(content: string): string {
        content = content
            .replace(/<span class="highlight-all" contenteditable="false">(.*?)<\/span>/g, '$1')
            .replace(/<span class="highlight-tag">(.*?)<\/span>/g, '$1')
            .replace(/<span class="highlight-condition">(.*?)<\/span>/g, '$1')
            .replace(/&#47#(if|list)/g, '/#$1')
            .replace(/<span class="highlight-symbol" contenteditable="false">\$\{<\/span>/g, '${')
            .replace(/<span class="highlight-symbol" contenteditable="false">}<\/span>/g, '}')
            .replace(/<span class="highlight-variable" contenteditable="false">(.*?)<\/span>/g, '$1')
            .replace(/&gt;/g, '>')
            .replace(/&lt;/g, '<')
        ;
        return content;
    }

    /**
     * Handle IF dialog result for both insert and editChip modes.
     */
    handleIfDialogResult(result: { code: string; condition: string; option: string }): void {
        if (!result || !result.code) {
            this.showIfDialog = false;
            this.editingChipElement = null;
            return;
        }

        if (this.ifDialogMode === 'editChip' && this.editingChipElement && this.tinyEditor?.editor) {
            // Edit mode: find the entire IF→ENDIF block and replace it with new code
            if (result.option === 'none') {
                this.removeIfBlock(this.editingChipElement);
            } else {
                this.replaceIfBlock(this.editingChipElement, result.code);
            }
            this.tinyEditor.editor.undoManager.add();
        } else {
            // Insert mode: insert generated FreeMarker code at cursor
            this.insertAsChipsOrRaw(result.code);
        }

        this.showIfDialog = false;
        this.editingChipElement = null;
    }

    /**
     * Find the matching ENDIF chip for a given IF chip by walking the DOM forward,
     * accounting for nested IF/ENDIF pairs.
     */
    private findMatchingEndif(ifChip: HTMLElement): HTMLElement | null {
        const body = this.tinyEditor?.editor?.getBody();
        if (!body) return null;

        // Collect all chips in document order
        const allChips: HTMLElement[] = Array.from(body.querySelectorAll('.te-chip'));
        const startIdx = allChips.indexOf(ifChip);
        if (startIdx === -1) return null;

        let depth = 0;
        for (let i = startIdx; i < allChips.length; i++) {
            const chipType = allChips[i].getAttribute('data-te-type');
            if (chipType === 'if') {
                depth++;
            } else if (chipType === 'endif') {
                depth--;
                if (depth === 0) {
                    return allChips[i];
                }
            }
        }
        return null;
    }

    /**
     * Replace the entire IF→ENDIF block (including content between) with new code.
     */
    private replaceIfBlock(ifChip: HTMLElement, newCode: string): void {
        const editor = this.tinyEditor?.editor;
        if (!editor) return;

        const endifChip = this.findMatchingEndif(ifChip);
        if (!endifChip) {
            // Fallback: just insert at cursor if we can't find the block
            this.insertAsChipsOrRaw(newCode);
            return;
        }

        // Create a range from the IF chip to the ENDIF chip and select it
        const rng = editor.dom.createRng();
        rng.setStartBefore(ifChip);
        rng.setEndAfter(endifChip);
        editor.selection.setRng(rng);

        // Replace selection with new chip-converted content
        const evt: any = { content: newCode, format: 'html' };
        editor.fire('BeforeSetContent', evt);
        editor.execCommand('mceInsertContent', false, evt.content);
    }

    /**
     * Remove the entire IF→ENDIF block (including content between).
     */
    private removeIfBlock(ifChip: HTMLElement): void {
        const editor = this.tinyEditor?.editor;
        if (!editor) return;

        const endifChip = this.findMatchingEndif(ifChip);
        if (!endifChip) {
            editor.dom.remove(ifChip);
            return;
        }

        const rng = editor.dom.createRng();
        rng.setStartBefore(ifChip);
        rng.setEndAfter(endifChip);
        editor.selection.setRng(rng);
        editor.execCommand('mceInsertContent', false, '');
    }

    /**
     * Handle table builder result when editing an existing table.
     */
    handleTableBuilderResult(html: string): void {
        if (html) {
            if (this.editingChipElement && this.tinyEditor?.editor) {
                // Edit mode: replace existing table element
                this.tinyEditor.editor.dom.replace(
                    this.tinyEditor.editor.dom.createFragment(html),
                    this.editingChipElement
                );
            } else {
                // Insert mode: insert new table at cursor position
                this.insertAsChipsOrRaw(html);
            }
        }
        this.showTableBuilderDialog = false;
        this.tableBuilderEditMode = false;
        this.tableBuilderExistingColumns = [];
        this.editingChipElement = null;
    }

    /**
     * Save fallback value for a variable chip from the dialog.
     */
    saveFallbackValue(): void {
        if (this.fallbackDialogChipElement) {
            const path = this.fallbackDialogPath;
            const fallback = this.fallbackDialogValue?.trim();
            let newExpr: string;
            let displayText: string;
            if (fallback) {
                newExpr = '${' + path + '!"' + fallback + '"}';
                displayText = path + ' !"' + fallback + '"';
            } else {
                newExpr = '${' + path + '}';
                displayText = path;
            }
            this.fallbackDialogChipElement.setAttribute('data-te-expr', btoa(newExpr));
            this.fallbackDialogChipElement.textContent = displayText;
            this.showFallbackDialog = false;
            this.fallbackDialogChipElement = null;
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
        this.autoSave.destroyAutoSaveSubscription();
        this.unsubscribeWidgetDropAction();
        if (this.commandSubscription) {
            this.commandSubscription.unsubscribe();
        }
    }

    unsubscribeWidgetDropAction() {
        if (this.widgetDataSubscription) {
            this.widgetDataSubscription.unsubscribe();
        }
    }

    stripHtmlTags(html: string): string {
        return html.replace(/<[^>]*>?/gm, ' ');
    }
}
